import type { CompanyListItemResource } from '@majuscule/contracts';

/** Lower case, without accents nor spaces around: « Société Générale » → « societe generale ». */
export function searchKey(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Companies matching the typed text (pure, unit-tested): every word must appear
 * in the name, the SIREN or the address, accents and case ignored. Names that
 * start with the text come first.
 */
export function filterCompanies<T extends Pick<CompanyListItemResource, 'name' | 'siren' | 'address'>>(
  companies: readonly T[],
  text: string,
): T[] {
  const query = searchKey(text);
  if (query === '') {
    return [...companies];
  }
  const words = query.split(/\s+/);
  const matches = companies.filter((company) => {
    const haystack = searchKey(
      [company.name, company.siren ?? '', company.address ?? ''].join(' '),
    );
    return words.every((word) => haystack.includes(word));
  });
  const starts = (company: T): number => (searchKey(company.name).startsWith(query) ? 0 : 1);
  return matches.sort((a, b) => starts(a) - starts(b));
}

/** « 12 RUE X 13420 GEMENOS » → « GEMENOS » : la ville suffit à distinguer deux homonymes. */
export function cityOf(address: string | null): string | null {
  if (address === null) return null;
  const match = /\d{5}\s+(.+)$/.exec(address.trim());
  return match?.[1] ?? null;
}
